import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageEditor from "./components/editor/PageEditor";
import { useSitePageDefinitions } from "./hooks/useSitePageDefinitions";
import { useWebsiteSeoData } from "./hooks/useWebsiteSeoData";
import {
  createSEOConfig,
  deleteSEOConfig,
  updateSEOConfig,
} from "./services/seoConfig";
import {
  EMPTY_FORM,
  pageLabel,
  robotsFromFlags,
  SEOForm,
  toForm,
} from "./types";
import { getPrepopulatedSEOForm } from "./utils/seoDefaults";

const FALLBACK_OG_IMAGE_URL = "https://i.postimg.cc/qqFRW3Cw/download-(15).jpg";

const SEOPageEditorRoute: React.FC = () => {
  const { siteId: siteIdParam, pageName } = useParams<{
    siteId: string;
    pageName: string;
  }>();
  const siteId = Number(siteIdParam);
  const navigate = useNavigate();

  const { site, configs, loading, reload } = useWebsiteSeoData(siteId);
  const { pages: pageDefinitions } = useSitePageDefinitions(siteId);
  const [form, setForm] = useState<SEOForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedConfig = configs.find(
    (config) => config.page_name === pageName,
  );

  useEffect(() => {
    if (loading) return;
    if (selectedConfig) {
      setForm(toForm(selectedConfig));
    } else {
      const activeDef = pageDefinitions.find(
        (def) => def.key === (pageName === "new" ? "" : pageName),
      );
      setForm(
        getPrepopulatedSEOForm({
          pageName: pageName === "new" ? "" : pageName || "",
          site,
          pageDefinition: activeDef,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pageName, selectedConfig?.id, pageDefinitions.length, site?.name]);

  const updateField = <K extends keyof SEOForm>(field: K, value: SEOForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const resetDefaults = () => {
    const targetKey = form.pageName || (pageName === "new" ? "" : pageName || "");
    const activeDef = pageDefinitions.find((def) => def.key === targetKey);
    const defaults = getPrepopulatedSEOForm({
      pageName: targetKey,
      site,
      pageDefinition: activeDef,
    });
    setForm(defaults);
    toast.success("Applied recommended SEO defaults");
  };

  const goToPages = () => navigate(`/configurations/seo-management/${siteId}`);

  const savePage = async (publish: boolean) => {
    if (!form.pageName.trim()) {
      toast.error("Enter a page name");
      return;
    }
    setSaving(true);
    const resolvedPageName = form.pageName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const payload = {
      site: siteId,
      page_name: resolvedPageName,
      meta_title_template: form.metaTitle,
      meta_keywords: form.metaKeywords,
      meta_description_template: form.metaDescription,
      focus_keyword: form.focusKeyword,
      related_keywords: form.relatedKeywords,
      og_title_template: form.ogTitle || null,
      og_type: form.ogType,
      og_description_template: form.ogDescription || null,
      og_image_url: form.ogImageUrl.trim() || FALLBACK_OG_IMAGE_URL,
      twitter_card_type: form.twitterCardType,
      twitter_site: form.twitterSite || null,
      twitter_creator: form.twitterCreator || null,
      twitter_title_template: form.twitterTitle,
      twitter_description_template: form.twitterDescription,
      twitter_image_url: form.twitterImageUrl,
      robots_meta: robotsFromFlags(form.noindex, form.nofollow),
      schema_type: form.schemaType,
      is_active: publish,
    };

    try {
      if (selectedConfig) {
        await updateSEOConfig(selectedConfig.id, payload);
      } else {
        await createSEOConfig(payload);
      }
      await reload();
      toast.success(
        selectedConfig
          ? "Page updated successfully"
          : "Page created successfully",
      );
      if (resolvedPageName !== pageName) {
        navigate(
          `/configurations/seo-management/${siteId}/${resolvedPageName}`,
          { replace: true },
        );
      }
    } catch (error) {
      console.error("Failed to save page SEO", error);
      toast.error("Failed to save page SEO");
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async () => {
    if (!selectedConfig) return;
    if (!window.confirm(`Delete ${pageLabel(selectedConfig.page_name)}?`))
      return;

    setDeleting(true);
    try {
      await deleteSEOConfig(selectedConfig.id);
      toast.success("Page deleted successfully");
      goToPages();
    } catch (error) {
      console.error("Failed to delete page SEO", error);
      toast.error("Failed to delete page");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageEditor
      selectedSite={site}
      selectedConfig={selectedConfig}
      pageDefinitions={pageDefinitions}
      form={form}
      saving={saving}
      deleting={deleting}
      onChange={updateField}
      onBack={goToPages}
      onSave={savePage}
      onDelete={deletePage}
      onResetDefaults={resetDefaults}
    />
  );
};

export default SEOPageEditorRoute;
